/*
Muestra los datos de Preview y Detail de consignación.
Autor: Benjamín León
Fecha: 2024-09-20
*/

import React from 'react'
import { Remark } from 'react-remark'

import {
  CompleteMark,
  CuadroPago,
  formatCurrency,
  formatDate,
  formatPeriod,
  formatWithThousandSep,
} from '../utils/utils'
import { formatDocumentTitle } from '../utils/documentTitleUtils'
import vehicleTypeTranslate from '../utils/vehicleTypeTranslate'
import { makeImage } from '../utils/makeImage'
import ConsignmentDet from '../interfaces/ConsignmentDet'
import fuelTranslate from '../utils/fuelTranslate'
import permitTranslate from '../utils/permitTranslate'
import colorTranslate from '../utils/colorTranslate'
import insuranceTranslate from '../utils/insuranceTranslate'
import maintenanceTranslate from '../utils/maintenanceTranslate'
import { DocStateSpecial } from '../enums/DocStates'
import DocToHeader from '../interfaces/DocInactive'

import { ClientFrame, ClientFrameType } from './ClientFrame'
import { OrganizationFrame, OrganizationFrameType } from './OrganizationFrame'
import DocNotes from './DocNotes'
import RectangleRow from './RectangleRow'
import { DisplayValue, DisplayValueCol } from './DisplayValue'
import { DocStatusHeader } from './DocStatusHeader'

const ConsignmentDetail: React.FC<{ consig: ConsignmentDet | null }> = ({ consig }) => {
  if (!consig) {
    return <div></div>
  }

  const tasacion_format = formatCurrency(consig.vehicle.appraisal)
  const odometer_format = formatWithThousandSep(consig.vehicle.odometer)
  const type_format = vehicleTypeTranslate[consig.vehicle.type] || consig.vehicle.type
  const fechaCrea = formatDate(consig.date)
  const fechaRevTecnica = formatPeriod(consig?.vehicle?.documents?.checkup ?? '0000-00-00')
  const urlImage = consig.organization.pictures.logotype
  const isAnulada = !!consig?.voidedAt
  const isArchivada = !!consig?.archivedAt
  const carmap = consig.carmap ?? false

  let docInactive: DocToHeader = {
    state: DocStateSpecial.Activo,
    date: '',
    cause: '',
    by: '',
  }

  if (isAnulada) {
    docInactive = {
      state: DocStateSpecial.Nulo,
      date: consig?.voidedAt ?? '',
      cause: consig.voidedCause ?? '',
      by: consig?.voidedBy_user ?? '',
    }
  } else if (isArchivada) {
    docInactive = {
      state: DocStateSpecial.Archivado,
      date: consig?.archivedAt ?? '',
      cause: consig?.archivedCause ?? '',
      by: consig?.archivedBy_user ?? '',
    }
  }
  const cityAndDate = `${consig.city}, ${fechaCrea}`
  const price_client_format = formatCurrency(consig?.price ?? 0)
  const price_publica_format = formatCurrency(consig.vehicle.price ?? 0)
  const fuel_format = fuelTranslate[consig.vehicle.fuel] || consig.vehicle.fuel
  const color_format = colorTranslate[consig.vehicle.color] || consig.vehicle.color
  const permit_format = permitTranslate[consig.vehicle.documents?.permit ?? ''] || consig.vehicle.documents?.permit
  const insurance_format =
    insuranceTranslate[consig?.vehicle.documents?.insurance ?? ''] || consig?.vehicle.documents?.insurance
  const maintenance_format =
    maintenanceTranslate[consig.vehicle.maintenance?.status ?? ''] || consig.vehicle.maintenance?.status

  const cuadro: CuadroPago = {
    minimum_format: formatWithThousandSep(consig?.minimum ?? 0),
    fee_format:
      consig?.fee.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }) ?? '0',

    charge_format: formatWithThousandSep(consig?.charge ?? 0),
    fee_iva: consig.feeIva ? ' más IVA' : '',
    minimum_iva: consig.feeIva ? ' más IVA' : '',
  }

  const body_first = CompleteMark(consig?.body_first ?? '', consig, cuadro)
  const body_second = CompleteMark(consig?.body_second ?? '', consig, cuadro)
  const footer = CompleteMark(consig?.footer ?? '', consig, cuadro)

  const clientFrame: ClientFrameType = {
    nin: consig?.client.nin,
    name: `${consig?.client.firstname} ${consig?.client.lastname}`,
    phone: consig?.client.phone,
    email: consig?.client.email,
    address: consig?.client.address,
  }
  const user = { name: consig.profile_name, phone: consig.user_phone ?? '' }
  const organizationFrame: OrganizationFrameType = {
    ...consig.organization,
    user: user,
  }

  // Format title using template configuration
  const defaultTitle = 'Contrato de Consignación'
  const formattedTitle = formatDocumentTitle(
    consig.titleConfig,
    defaultTitle,
    0 // Consignments don't have numbers
  )

  return (
    <>
      <DocStatusHeader docName="consignación" state={docInactive} />
      <div className="flex items-center justify-between p-0 w-full">
        {/* Image Section */}
        <div className="flex-shrink-0">
          <img src={makeImage(urlImage)} alt="Logo" className="h-12" />
        </div>

        {/* Title Section - centered */}
        <div className="flex-grow text-center">
          <div className="text-2xl font-semibold">{formattedTitle}</div>
        </div>

        {/* Empty space for balance */}
        <div className="flex-shrink-0 w-12"></div>
      </div>

      <div className="flex justify-end text-xl">
        <div>{cityAndDate}</div>
      </div>
      <hr className="w-full border-t border-black-500 my-2" />
      <OrganizationFrame organization={organizationFrame} />

      <hr className="w-full border-t border-black-500 my-2" />
      <div>
        <Remark>{body_first}</Remark>
      </div>

      <hr className="w-full border-t border-black-500 my-2" />
      <ClientFrame client={clientFrame} />
      <hr className="w-full border-t border-black-500 my-2" />
      <div>
        <Remark>{body_second}</Remark>
      </div>
      <hr className="w-full border-t border-black-500 my-2" />
      <div className="mb-2">
        <span className="font-bold">Datos del Vehículo</span>
      </div>
      <div className="grid grid-cols-2 gap-x-2">
        <DisplayValueCol label="Tipo" value={type_format} />
        <DisplayValueCol label="Patente" value={consig.vehicle.plate} />

        <DisplayValueCol label="Marca" value={consig?.vehicle.brand} />
        <DisplayValueCol label="N° motor" value={consig?.vehicle.engineId} />

        <DisplayValueCol label="Modelo" value={`${consig?.vehicle.model}`} />
        <DisplayValueCol label="N° Chassis" value={consig?.vehicle.chassisId} />

        <DisplayValueCol label="Versión" value={consig?.vehicle.version} />
        <DisplayValueCol label="Combustible" value={fuel_format} />

        <DisplayValueCol label="Año" value={consig?.vehicle.year} />
        <DisplayValueCol label="Rev. Técnica" value={fechaRevTecnica} />

        <DisplayValueCol label="Color" value={color_format} />
        <DisplayValueCol label="Permiso" value={permit_format} />

        <DisplayValueCol label="Kilometraje" value={odometer_format} />
        <DisplayValueCol label="Seguro oblig." value={insurance_format} />

        <DisplayValueCol label="Dueños" value={consig.vehicle.owners} />
        <DisplayValueCol label="Tasación fiscal" value={tasacion_format} />

        <DisplayValueCol label="Copia Llaves" value={consig.vehicle.keys} />
        <DisplayValueCol label="Consignado por" value={consig.profile_name} />
        <DisplayValueCol label="Mantenciones" value={maintenance_format} />
        <DisplayValueCol label="" value="" />
        <DisplayValueCol label="" value={consig.vehicle.maintenance?.comments} />
      </div>
      <hr className="w-full border-t border-black-500 my-2" />
      <div className="flex justify-between items-center">
        <div className="text-xl">
          <DisplayValue label="Precio cliente" value={price_client_format} />
        </div>
        <div className="text-xl">
          <DisplayValue label="Precio publicación" value={price_publica_format} />
        </div>
      </div>
      {consig?.paymentShow && (
        <>
          <hr className="w-full border-t border-black-500 my-2" />

          <div className="grid grid-cols gap-4 border-2 p-2">
            <div className="font-bold text-xl ">Pagos</div>
            <div className="grid grid-cols-2 gap-2">
              <DisplayValue label="Comisión mínima" value={`$${cuadro.minimum_format}${cuadro.minimum_iva}`} />
              <DisplayValue label="Porcentaje de comisión" value={`${cuadro.fee_format}%${cuadro.fee_iva}`} />
              <DisplayValue label="Cobro por retiro anticipado" value={`$${cuadro.charge_format}`} />
            </div>
          </div>
        </>
      )}

      <hr className="w-full border-t border-black-500 my-2" />
      <DocNotes notes={consig?.notes} />
      <hr className="w-full border-t border-black-500 my-2" />

      {carmap && (
        <>
          <div>
            <div className="text-2xl">Anotaciones</div>
            <img src="/assets/images/vehicle-views.svg" alt="Esquema del vehículo" />
            <RectangleRow />
          </div>
          <hr className="w-full border-t border-black-500 my-2" />
        </>
      )}
      <Remark>{footer}</Remark>
    </>
  )
}

export default ConsignmentDetail
